/**
 * Cookie consent banner — UK GDPR / PECR compliant.
 *
 * Shows on first visit (or after the policy version bumps server-side). Users
 * can:
 *   - Accept all  → analytics + marketing on
 *   - Reject all  → only strictly-necessary cookies (always on)
 *   - Preferences → modal with per-category toggles
 *
 * Choices are stored in localStorage for runtime gating (so we don't re-prompt
 * on every page) AND POST'd to /api/cookies/consent so we have a server-side
 * audit trail (required to prove consent under UK GDPR).
 *
 * The "Cookie settings" link in the footer lets users re-open the preferences
 * modal at any time to update or withdraw consent.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";
import { axios, API, logError } from "../lib/api";
import { useAuth } from "../auth/AuthProvider";

const STORAGE_KEY = "toycycle_cookie_consent_v1";

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const writeStored = (obj) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(obj)); }
  catch (err) { logError("CookieBanner: localStorage write failed", err); }
};

const DEFAULT_CHOICES = { necessary: true, analytics: false, marketing: false };

export const CookieBanner = () => {
  const { user } = useAuth();
  const [serverVersion, setServerVersion] = useState(null);
  const [open, setOpen] = useState(false);             // banner visible
  const [prefsOpen, setPrefsOpen] = useState(false);   // preferences modal
  const [choices, setChoices] = useState(DEFAULT_CHOICES);

  // Listen for "open me" requests from the footer link.
  useEffect(() => {
    const handler = () => {
      const stored = readStored();
      if (stored?.choices) setChoices(stored.choices);
      setPrefsOpen(true);
    };
    window.addEventListener("toycycle:open-cookie-prefs", handler);
    return () => window.removeEventListener("toycycle:open-cookie-prefs", handler);
  }, []);

  // Decide whether to show the banner on mount / policy-version change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/cookies/version`);
        if (cancelled) return;
        const policyVersion = r.data?.version;
        setServerVersion(policyVersion);
        const stored = readStored();
        if (!stored || stored.policy_version !== policyVersion) {
          setOpen(true);
        } else if (stored.choices) {
          setChoices(stored.choices);
        }
      } catch (err) {
        logError("CookieBanner: version fetch failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = useCallback(async (next, method) => {
    const stored = readStored();
    const payload = {
      consent_id: stored?.consent_id || null,
      user_id: user?.id || null,
      choices: next,
      method,
      policy_version: serverVersion || "unknown",
    };
    try {
      const r = await axios.post(`${API}/cookies/consent`, payload);
      writeStored({
        consent_id: r.data?.consent_id,
        policy_version: r.data?.policy_version,
        choices: next,
        recorded_at: new Date().toISOString(),
      });
    } catch (err) {
      // Even if the server call fails, honour the user's choice locally so
      // the banner doesn't keep nagging them. Server retry can be added later.
      writeStored({
        consent_id: stored?.consent_id || null,
        policy_version: serverVersion || "unknown",
        choices: next,
        recorded_at: new Date().toISOString(),
      });
      logError("CookieBanner: server save failed", err);
    }
    setChoices(next);
    setOpen(false);
    setPrefsOpen(false);
  }, [user, serverVersion]);

  const acceptAll = () => submit({ necessary: true, analytics: true, marketing: true }, "banner_accept_all");
  const rejectAll = () => submit({ necessary: true, analytics: false, marketing: false }, "banner_reject_all");
  const saveCustom = () => submit({ ...choices, necessary: true }, "preferences_modal");

  if (!open && !prefsOpen) return null;

  return (
    <>
      {/* Banner */}
      {open && !prefsOpen && (
        <div
          className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6"
          role="dialog"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-desc"
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto bg-white border-2 border-gray-900 rounded-2xl shadow-[6px_6px_0px_0px_rgba(17,24,39,1)] p-5 sm:p-6">
            <div className="flex flex-col md:flex-row md:items-start gap-4">
              <span className="w-12 h-12 rounded-full bg-[#FFD93D] border-2 border-gray-900 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-6 h-6 text-gray-900" />
              </span>
              <div className="flex-1 min-w-0">
                <h2 id="cookie-banner-title" className="font-bold mb-1">
                  We use cookies
                </h2>
                <p id="cookie-banner-desc" className="text-sm text-gray-700">
                  Strictly necessary cookies keep ToyCycleHub running. With your permission, we
                  also use analytics cookies to improve the site, and marketing cookies to show
                  you relevant promotions. See our{" "}
                  <Link to="/privacy" className="font-bold underline text-[#FF6B6B]">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4 justify-end">
              <button
                type="button"
                onClick={() => setPrefsOpen(true)}
                className="px-4 py-2 text-sm font-bold rounded-full border-2 border-gray-900 bg-white"
                data-testid="cookie-banner-prefs"
              >
                Preferences
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="px-4 py-2 text-sm font-bold rounded-full border-2 border-gray-900 bg-white"
                data-testid="cookie-banner-reject"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="px-4 py-2 text-sm font-bold rounded-full border-2 border-gray-900 bg-[#FF6B6B] text-white shadow-[3px_3px_0px_0px_rgba(17,24,39,1)]"
                data-testid="cookie-banner-accept"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences modal */}
      {prefsOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
          role="dialog"
          aria-labelledby="cookie-prefs-title"
          aria-modal="true"
          data-testid="cookie-prefs-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setPrefsOpen(false); }}
        >
          <div className="w-full max-w-md bg-white border-2 border-gray-900 rounded-2xl shadow-[6px_6px_0px_0px_rgba(17,24,39,1)] p-6">
            <div className="flex items-start justify-between mb-4">
              <h2 id="cookie-prefs-title" className="font-bold text-xl">Cookie preferences</h2>
              <button
                type="button"
                onClick={() => setPrefsOpen(false)}
                aria-label="Close cookie preferences"
                className="p-1"
                data-testid="cookie-prefs-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <CookieRow
              label="Strictly necessary"
              help="Required for login, checkout, and basic site security. Cannot be turned off."
              checked
              disabled
            />
            <CookieRow
              label="Analytics"
              help="Anonymous usage stats so we can fix bugs and improve the site."
              checked={choices.analytics}
              onChange={(v) => setChoices({ ...choices, analytics: v })}
              testId="cookie-pref-analytics"
            />
            <CookieRow
              label="Marketing"
              help="Helps us show relevant promotions on and off ToyCycleHub."
              checked={choices.marketing}
              onChange={(v) => setChoices({ ...choices, marketing: v })}
              testId="cookie-pref-marketing"
            />

            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={rejectAll}
                className="px-4 py-2 text-sm font-bold rounded-full border-2 border-gray-900 bg-white"
                data-testid="cookie-prefs-reject"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={saveCustom}
                className="px-4 py-2 text-sm font-bold rounded-full border-2 border-gray-900 bg-[#FF6B6B] text-white shadow-[3px_3px_0px_0px_rgba(17,24,39,1)]"
                data-testid="cookie-prefs-save"
              >
                Save preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const CookieRow = ({ label, help, checked, onChange, disabled, testId }) => (
  <div className="flex items-start justify-between gap-3 py-3 border-b border-gray-100">
    <div className="flex-1 min-w-0">
      <p className="font-bold text-sm">{label}</p>
      <p className="text-xs text-gray-500 mt-0.5">{help}</p>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      data-testid={testId}
      className={`relative inline-flex w-11 h-6 rounded-full border-2 border-gray-900 transition-colors flex-shrink-0 ${
        checked ? "bg-[#10B981]" : "bg-gray-200"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full border border-gray-900 transition-transform ${
          checked ? "translate-x-5" : ""
        }`}
      />
    </button>
  </div>
);
